/**
 * Ledger HTML report builder + per-period helpers.
 *
 * Web port (simplified) of mobile's
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/ledger/ledgerPdf.ts
 *
 * Generates a print-ready HTML report:
 *   - Cover: org + period label + GSTIN
 *   - Summary: Total in / Total out / Net balance
 *   - Detail table: every transaction in the period (date, party,
 *     project, category, method, amount)
 *   - Summary tables: by project, by category, by payment method
 *
 * The caller can hand the returned string to `openHtmlForPrint()`
 * from projectReports.ts so the user gets a Save-as-PDF dialog.
 */

import {
  categoryLabel,
  paymentMethodLabel,
} from '@/lib/transactions';
import {
  isCountedInTotals,
  txDateMs,
  type TransactionItem,
} from '@/lib/useProjectTransactions';

// ───── Period helpers ─────

export type LedgerPeriodKey =
  | 'all'
  | 'today'
  | 'this_week'
  | 'this_month'
  | 'last_month'
  | 'custom';

export type LedgerPeriod = {
  key: LedgerPeriodKey;
  /** Inclusive lower bound (epoch ms). null = no lower bound. */
  fromMs: number | null;
  /** EXCLUSIVE upper bound (epoch ms). null = no upper bound. */
  toMs: number | null;
  /** Human label e.g. "This month" or "1 May 2026 → 24 May 2026". */
  label: string;
};

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function startOfMonth(year: number, month: number): Date {
  return new Date(year, month, 1, 0, 0, 0, 0);
}

const PERIOD_LABELS: Record<LedgerPeriodKey, string> = {
  all:         'All time',
  today:       'Today',
  this_week:   'This week',
  this_month:  'This month',
  last_month:  'Last month',
  custom:      'Custom range',
};

export function buildLedgerPeriod(
  key: LedgerPeriodKey,
  custom?: { fromKey?: string; toKey?: string },
): LedgerPeriod {
  const now = new Date();
  if (key === 'all') {
    return { key, fromMs: null, toMs: null, label: PERIOD_LABELS.all };
  }
  if (key === 'today') {
    const from = startOfDay(now).getTime();
    const to = from + 86400000;
    return { key, fromMs: from, toMs: to, label: 'Today' };
  }
  if (key === 'this_week') {
    // Week starts Monday (Indian business norm).
    const d = startOfDay(now);
    const dayOfWeek = (d.getDay() + 6) % 7; // 0 = Mon
    d.setDate(d.getDate() - dayOfWeek);
    return {
      key,
      fromMs: d.getTime(),
      toMs: d.getTime() + 7 * 86400000,
      label: 'This week',
    };
  }
  if (key === 'this_month') {
    const from = startOfMonth(now.getFullYear(), now.getMonth());
    const to = startOfMonth(now.getFullYear(), now.getMonth() + 1);
    return {
      key,
      fromMs: from.getTime(),
      toMs: to.getTime(),
      label: 'This month',
    };
  }
  if (key === 'last_month') {
    const from = startOfMonth(now.getFullYear(), now.getMonth() - 1);
    const to = startOfMonth(now.getFullYear(), now.getMonth());
    return {
      key,
      fromMs: from.getTime(),
      toMs: to.getTime(),
      label: 'Last month',
    };
  }
  // Custom — parse YYYY-MM-DD inputs.
  const fromMs = custom?.fromKey ? parseDateKey(custom.fromKey)?.getTime() ?? null : null;
  const toMsRaw = custom?.toKey ? parseDateKey(custom.toKey)?.getTime() ?? null : null;
  // The "to" input is inclusive in the UI but exclusive internally —
  // bump by one day so a "to: 24 May" range catches all of 24 May.
  const toMs = toMsRaw != null ? toMsRaw + 86400000 : null;
  const label =
    fromMs && toMsRaw
      ? `${formatDateMs(fromMs)} → ${formatDateMs(toMsRaw)}`
      : fromMs
        ? `From ${formatDateMs(fromMs)}`
        : toMsRaw
          ? `Until ${formatDateMs(toMsRaw)}`
          : 'Custom range';
  return { key: 'custom', fromMs, toMs, label };
}

function parseDateKey(key: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return null;
  const [y, m, d] = key.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

export function periodFilter(
  txns: TransactionItem[],
  period: LedgerPeriod,
): TransactionItem[] {
  if (period.fromMs == null && period.toMs == null) return txns;
  return txns.filter((t) => {
    const ms = txDateMs(t);
    if (period.fromMs != null && ms < period.fromMs) return false;
    if (period.toMs != null && ms >= period.toMs) return false;
    return true;
  });
}

// ───── Totals ─────

export type LedgerTotals = {
  inPaise: number;
  outPaise: number;
  netPaise: number;
  count: number;
};

/** Sum amounts in *rupees* directly (transactions are stored in
 *  rupees, not paise — see TransactionItem.amount). Returns a
 *  positive number for `in` and `out` (sign is implicit in the type). */
export function computeLedgerTotals(txns: TransactionItem[]): LedgerTotals {
  let inAmt = 0;
  let outAmt = 0;
  let counted = 0;
  for (const t of txns) {
    // Apply the cash-flow gate so settlement bills (advance
    // children) don't double-count the cash that already left
    // with the parent advance. Same gate the on-screen project
    // totals use — keeps the PDF and the UI agreeing on a number.
    if (!isCountedInTotals(t)) continue;
    counted += 1;
    const tp = normalizeType(t.type);
    if (tp === 'in') inAmt += t.amount;
    else if (tp === 'out') outAmt += t.amount;
  }
  return {
    inPaise: inAmt,
    outPaise: outAmt,
    netPaise: inAmt - outAmt,
    count: counted,
  };
}

function normalizeType(raw: string | undefined): 'in' | 'out' | null {
  if (!raw) return null;
  const r = raw.toLowerCase();
  if (r === 'payment_in' || r === 'income' || r === 'in') return 'in';
  if (r === 'payment_out' || r === 'expense' || r === 'out') return 'out';
  return null;
}

// ───── HTML report ─────

function fmtDateMs(ms: number): string {
  return new Date(ms).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateMs(ms: number): string {
  return new Date(ms).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function fmtInr(rs: number): string {
  return '₹' + Math.round(rs).toLocaleString('en-IN');
}

function escape(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export type LedgerReportInput = {
  orgName: string;
  orgGstin?: string;
  orgAddress?: string;
  period: LedgerPeriod;
  txns: TransactionItem[];
  projectNameById: Map<string, string>;
  generatedOn: string;
};

export function buildLedgerReportHtml(input: LedgerReportInput): string {
  const { orgName, orgGstin, orgAddress, period, txns, projectNameById, generatedOn } = input;
  const totals = computeLedgerTotals(txns);

  // Sort by date ASC for the detail table (oldest-first reads more
  // naturally on a printed statement).
  const sorted = [...txns].sort((a, b) => txDateMs(a) - txDateMs(b));

  // Build running balance per row.
  let running = 0;
  const detailRows = sorted.map((t) => {
    const tp = normalizeType(t.type);
    const signed = tp === 'in' ? t.amount : tp === 'out' ? -t.amount : 0;
    running += signed;
    const isIn = tp === 'in';
    return `
      <tr>
        <td class="t-date">${escape(fmtDateMs(txDateMs(t)))}</td>
        <td>
          <div class="t-title">${escape(t.description || t.partyName || 'Transaction')}</div>
          <div class="t-meta">${escape(t.partyName || '—')}${
            t.referenceNumber ? ` &middot; Ref ${escape(t.referenceNumber)}` : ''
          }</div>
        </td>
        <td>${escape(projectNameById.get(t.projectId) || t.projectId.slice(-6))}</td>
        <td>${escape(categoryLabel(t.category as never) || '—')}</td>
        <td>${escape(paymentMethodLabel(t.paymentMethod as never) || '—')}</td>
        <td class="num" style="color:${isIn ? '#0F9D58' : '#B91C1C'};font-weight:700">
          ${isIn ? '+' : '−'}${fmtInr(t.amount)}
        </td>
        <td class="num" style="font-weight:600">${fmtInr(running)}</td>
      </tr>`;
  });

  // Summary by project.
  const byProject = new Map<
    string,
    { name: string; inAmt: number; outAmt: number; count: number }
  >();
  for (const t of txns) {
    const key = t.projectId;
    const name = projectNameById.get(key) || key.slice(-6);
    const cur = byProject.get(key) ?? { name, inAmt: 0, outAmt: 0, count: 0 };
    const tp = normalizeType(t.type);
    if (tp === 'in') cur.inAmt += t.amount;
    else if (tp === 'out') cur.outAmt += t.amount;
    cur.count += 1;
    byProject.set(key, cur);
  }
  const projectRows = Array.from(byProject.values())
    .sort((a, b) => b.inAmt + b.outAmt - (a.inAmt + a.outAmt))
    .map(
      (p) => `
      <tr>
        <td>${escape(p.name)}</td>
        <td class="num">${p.count}</td>
        <td class="num" style="color:#0F9D58">${fmtInr(p.inAmt)}</td>
        <td class="num" style="color:#B91C1C">${fmtInr(p.outAmt)}</td>
        <td class="num" style="font-weight:700">${fmtInr(p.inAmt - p.outAmt)}</td>
      </tr>`,
    )
    .join('');

  // Summary by category.
  const byCategory = new Map<string, { in: number; out: number; count: number }>();
  for (const t of txns) {
    const key = t.category || 'others';
    const cur = byCategory.get(key) ?? { in: 0, out: 0, count: 0 };
    const tp = normalizeType(t.type);
    if (tp === 'in') cur.in += t.amount;
    else if (tp === 'out') cur.out += t.amount;
    cur.count += 1;
    byCategory.set(key, cur);
  }
  const categoryRows = Array.from(byCategory.entries())
    .sort((a, b) => b[1].in + b[1].out - (a[1].in + a[1].out))
    .map(
      ([k, v]) => `
      <tr>
        <td>${escape(categoryLabel(k as never))}</td>
        <td class="num">${v.count}</td>
        <td class="num" style="color:#0F9D58">${fmtInr(v.in)}</td>
        <td class="num" style="color:#B91C1C">${fmtInr(v.out)}</td>
      </tr>`,
    )
    .join('');

  // Summary by payment method.
  const byMethod = new Map<string, { in: number; out: number; count: number }>();
  for (const t of txns) {
    const key = t.paymentMethod || 'unknown';
    const cur = byMethod.get(key) ?? { in: 0, out: 0, count: 0 };
    const tp = normalizeType(t.type);
    if (tp === 'in') cur.in += t.amount;
    else if (tp === 'out') cur.out += t.amount;
    cur.count += 1;
    byMethod.set(key, cur);
  }
  const methodRows = Array.from(byMethod.entries())
    .sort((a, b) => b[1].in + b[1].out - (a[1].in + a[1].out))
    .map(
      ([k, v]) => `
      <tr>
        <td>${escape(k === 'unknown' ? '—' : paymentMethodLabel(k as never))}</td>
        <td class="num">${v.count}</td>
        <td class="num" style="color:#0F9D58">${fmtInr(v.in)}</td>
        <td class="num" style="color:#B91C1C">${fmtInr(v.out)}</td>
      </tr>`,
    )
    .join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escape(orgName)} — Ledger ${escape(period.label)}</title>
<style>
@page { size: A4; margin: 12mm 10mm; }
* { box-sizing: border-box; }
body {
  margin: 0; padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #15171A; font-size: 10pt;
  -webkit-font-smoothing: antialiased;
}
.muted { color: #475569; font-size: 9pt; }
.kicker {
  font-family: Menlo, Consolas, monospace;
  font-size: 7.5pt; font-weight: 700;
  color: #94A3B8; letter-spacing: 1.4px;
}
.num { text-align: right; font-variant-numeric: tabular-nums; }

.cover {
  border-bottom: 1px solid #E2E8F0;
  padding-bottom: 12pt; margin-bottom: 14pt;
}
.brand {
  font-family: Menlo, Consolas, monospace;
  font-size: 8pt; font-weight: 700;
  color: #DC2626; letter-spacing: 1.6px;
}
h1 {
  font-size: 20pt; margin: 6pt 0 4pt 0;
  font-weight: 800; letter-spacing: -0.5px;
}

section { margin-bottom: 14pt; page-break-inside: auto; }
h2 {
  font-family: Menlo, Consolas, monospace;
  font-size: 8pt; font-weight: 700;
  color: #94A3B8; letter-spacing: 1.4px;
  text-transform: uppercase;
  margin: 0 0 6pt 0; padding-bottom: 4pt;
  border-bottom: 1px solid #EEF2F7;
  page-break-after: avoid;
}
.cover { page-break-inside: avoid; }

table { width: 100%; border-collapse: collapse; }
thead { display: table-header-group; }
tr { page-break-inside: avoid; }
th, td {
  padding: 6pt 8pt; text-align: left;
  border-bottom: 1px solid #EEF2F7;
  font-size: 9.5pt; vertical-align: top;
}
th {
  font-family: Menlo, Consolas, monospace;
  font-size: 7.5pt; font-weight: 700;
  color: #475569; letter-spacing: 1.2px;
  background: #F8FAFC; border-bottom: 1px solid #E2E8F0;
}

.totals {
  display: flex; gap: 8pt;
}
.total-cell {
  flex: 1; padding: 10pt;
  background: #F8FAFC; border: 1px solid #EEF2F7;
}
.total-cell.in  { background: #E3F5EB; border-color: #C7E8D5; }
.total-cell.out { background: #FEE2E2; border-color: #FCA5A5; }
.total-cell.bal { background: #DBEAFE; border-color: #BFDBFE; }
.total-val {
  font-size: 18pt; font-weight: 800;
  letter-spacing: -0.4px;
}
.total-label {
  font-family: Menlo, Consolas, monospace;
  font-size: 7.5pt; font-weight: 700; letter-spacing: 1.2px;
  margin-top: 2pt;
}

.t-date {
  font-family: Menlo, Consolas, monospace;
  font-size: 8pt; font-weight: 600; color: #475569;
  letter-spacing: 0.6px; white-space: nowrap;
}
.t-title { font-weight: 700; font-size: 10pt; line-height: 1.3; }
.t-meta {
  font-family: Menlo, Consolas, monospace;
  font-size: 7.5pt; color: #94A3B8;
  letter-spacing: 0.6px; margin-top: 2pt;
}
.empty {
  text-align: center; color: #94A3B8; font-style: italic;
  padding: 14pt 4pt !important;
}
</style>
</head>
<body>
  <header class="cover">
    <div class="brand">INTERIOR OS &middot; LEDGER STATEMENT</div>
    <h1>${escape(orgName)}</h1>
    ${orgAddress ? `<div class="muted">${escape(orgAddress)}</div>` : ''}
    ${orgGstin ? `<div class="muted">GSTIN: ${escape(orgGstin)}</div>` : ''}
    <div class="muted">Period: ${escape(period.label)}</div>
    <div class="muted">Generated ${escape(generatedOn)}</div>
  </header>

  <section>
    <h2>Summary</h2>
    <div class="totals">
      <div class="total-cell in">
        <div class="total-val">${fmtInr(totals.inPaise)}</div>
        <div class="total-label">TOTAL IN</div>
      </div>
      <div class="total-cell out">
        <div class="total-val">${fmtInr(totals.outPaise)}</div>
        <div class="total-label">TOTAL OUT</div>
      </div>
      <div class="total-cell bal">
        <div class="total-val">${fmtInr(totals.netPaise)}</div>
        <div class="total-label">NET BALANCE</div>
      </div>
    </div>
    <div class="muted" style="margin-top:6pt">
      ${totals.count} transaction${totals.count === 1 ? '' : 's'} in this period.
    </div>
  </section>

  <section>
    <h2>Transactions</h2>
    ${
      detailRows.length === 0
        ? '<div class="empty">No transactions in this period.</div>'
        : `<table>
             <thead>
               <tr>
                 <th>Date</th>
                 <th>Description</th>
                 <th>Project</th>
                 <th>Category</th>
                 <th>Method</th>
                 <th class="num">Amount</th>
                 <th class="num">Balance</th>
               </tr>
             </thead>
             <tbody>${detailRows.join('')}</tbody>
           </table>`
    }
  </section>

  ${
    byProject.size > 0
      ? `<section>
           <h2>By project</h2>
           <table>
             <thead>
               <tr>
                 <th>Project</th>
                 <th class="num">Count</th>
                 <th class="num">In</th>
                 <th class="num">Out</th>
                 <th class="num">Net</th>
               </tr>
             </thead>
             <tbody>${projectRows}</tbody>
           </table>
         </section>`
      : ''
  }

  ${
    byCategory.size > 0
      ? `<section>
           <h2>By category</h2>
           <table>
             <thead>
               <tr>
                 <th>Category</th>
                 <th class="num">Count</th>
                 <th class="num">In</th>
                 <th class="num">Out</th>
               </tr>
             </thead>
             <tbody>${categoryRows}</tbody>
           </table>
         </section>`
      : ''
  }

  ${
    byMethod.size > 0
      ? `<section>
           <h2>By payment method</h2>
           <table>
             <thead>
               <tr>
                 <th>Method</th>
                 <th class="num">Count</th>
                 <th class="num">In</th>
                 <th class="num">Out</th>
               </tr>
             </thead>
             <tbody>${methodRows}</tbody>
           </table>
         </section>`
      : ''
  }

  <div class="muted" style="text-align:center;margin-top:14pt">
    Generated by InteriorIO &middot; ${escape(generatedOn)}
  </div>
</body>
</html>`;
}
